import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_MAGENTA } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/on-and-on.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/on-and-on.mp3',
  attribution:
    '"On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('on-and-on', variant),
    slug: buildVariantSlug('on-and-on', variant),
    title: buildVariantTitle('On & On', variant),
    description:
      'Easy intro track. 86 BPM electro-pop with vocals - downbeat-driven groove that opens into a syncopated chorus.',
    bpm: onsets.bpm,
    difficulty: 1,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_MAGENTA,
    audio,
  });

  // 60s @ 86 BPM ≈ 86 beats. Sections sized to match the actual song
  // structure inside our cut: intro vocal phrase, first chorus, second
  // chorus, vocal hook out.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 48, kind: 'chorus' });
  builder.section({ startBeat: 48, endBeat: 64, kind: 'bridge' });
  builder.section({ startBeat: 64, endBeat: 86, kind: 'chorus' });

  // Verse: snare backbeat (the song's signature "On... and on..." vocal lands
  // here). Loose minSpacing so the player gets time to read each one.
  const versePads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['snare'],
    minIntensity: 0.25,
    minSpacing: 1.5,
  });

  // Chorus: heavier - kick + snare hits (the four-on-the-floor + clap).
  const chorusPads = variantSelectBeats(variant, onsets, {
    fromBeat: 16,
    untilBeat: 48,
    kinds: ['kick', 'snare'],
    minIntensity: 0.25,
    minSpacing: 1,
  });

  // Bridge: thin out so the player gets a breather; lead-only adds the
  // melodic vocal line back in.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 48,
    untilBeat: 64,
    kinds: ['lead'],
    minIntensity: 0.3,
    minSpacing: 2,
  });

  // Final chorus reprise: same density as the first.
  const finalChorusPads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 84,
    kinds: ['kick', 'snare'],
    minIntensity: 0.25,
    minSpacing: 1,
  });

  const padBeats = mergeBeats(versePads, chorusPads, bridgePads, finalChorusPads);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // Auto-fill long quiet stretches with mash chambers so the player has
  // something to do when the chart goes sparse (vocal-only sections, the
  // gap between bridge and final chorus, etc).
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 84,
    minGapBeats: 6,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(86);
  return builder.build();
}

export const ON_AND_ON_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const ON_AND_ON_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(ON_AND_ON_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
